/**
 * Payment routes where Wingify must never run: Stripe checkout and order
 * payment/confirmation, including subpaths.
 *
 * Why: PCI DSS 4.0 req 6.4.3 covers every script on a page that hosts payment
 * entry, and Wingify's tag can't carry an integrity hash (the vendor changes it
 * server-side). This is not what clears the ASV scan; the ASYNC loader in
 * {@link WingifySmartCode} does that. If SecOps confirms 6.4.3 doesn't apply to
 * us, delete this exclusion (this file, wingify-excluded-path-navigation.tsx and
 * their tests).
 *
 * Trade-off: Wingify can't run A/B tests or record conversion goals on these
 * routes, including the order confirmation page.
 */
export const WINGIFY_EXCLUDED_PATH = /^\/(checkout|orders)(\/|$)/;

/** Marks a `window._wingify_code` placeholder set by {@link wingifyGuardScript}. */
const EXCLUDED_FLAG = "_upExcluded";

declare global {
  interface Window {
    _wingify_code?: { [EXCLUDED_FLAG]?: boolean };
  }
}

/**
 * Inline script that must run before SmartCode. On excluded paths it occupies
 * `window._wingify_code`, which SmartCode treats as "already initialised", so
 * the loader never injects the Wingify tag or its anti-flicker style.
 *
 * This is NOT a Wingify-supported API. It was derived from the snippet that
 * `wingify-smartcode-nextjs` generates (`dist/smartCode.generated.js`, checked
 * against 1.5.0 and 1.5.1), which starts `window._wingify_code||function(){...}`
 * and later calls `_wingify_code.finished()`. Wingify's PCI help article
 * doesn't describe page exclusion. The snippet changed in two of the first five
 * releases, so when the package is upgraded, `wingify-smart-code.markup.test.tsx`
 * (which runs the real package output) is what tells you whether this still
 * works. If it breaks, Wingify loads on payment routes again; nothing else fails.
 */
export const wingifyGuardScript = `if(${WINGIFY_EXCLUDED_PATH}.test(location.pathname))window._wingify_code={${EXCLUDED_FLAG}:!0,finished:function(){return!0}};`;

/** Whether real Wingify SmartCode (not the exclusion placeholder) ran in this document. */
export function isWingifyLoaded(): boolean {
  const code = window._wingify_code;
  return Boolean(code) && !code?.[EXCLUDED_FLAG];
}
